import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";
import { devWarn } from "@/lib/devLog";

const STORAGE_KEY = "dg_cookie_consent_v1";

/**
 * GDPR-compliant cookie banner.
 *
 * Storage: { necessary: true, analytics: bool, ts: ISOString }
 * Available via window event "dg-cookie-consent-changed" so analytics
 * scripts can lazily mount themselves only after explicit consent.
 *
 * Essential cookies (auth + dg_auth_hint) do not require consent and are
 * therefore not gated by this banner.
 */
export default function CookieBanner() {
  const { t } = useTranslation();
  // Compute initial visibility synchronously so the effect never has to
  // call setState (satisfies the strict react-hooks/set-state-in-effect rule).
  const [open, setOpen] = useState(() => {
    try {
      return !localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      devWarn("CookieBanner: localStorage read failed", err);
      return true;
    }
  });
  const [showDetails, setShowDetails] = useState(false);

  // When the banner is visible, reserve bottom space on the body so the banner
  // never overlaps interactive UI (CTAs, opportunity cards, discovery links).
  useEffect(() => {
    if (!open) return;
    const apply = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      document.body.style.setProperty(
        "--cookie-banner-h",
        isMobile ? "260px" : "210px",
      );
      document.body.style.paddingBottom = "var(--cookie-banner-h, 0px)";
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
      document.body.style.removeProperty("--cookie-banner-h");
    };
  }, [open]);

  const persist = (analytics) => {
    const value = { necessary: true, analytics, ts: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (err) {
      devWarn("CookieBanner: localStorage write failed", err);
    }
    window.dispatchEvent(new CustomEvent("dg-cookie-consent-changed", { detail: value }));
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label={t("cookie.aria", { defaultValue: "Cookie consent" })}
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-5 md:bottom-5 md:max-w-sm z-50"
      data-testid="cookie-banner"
    >
      <div className="card-clay p-4 shadow-xl bg-white">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[#E0EBD3] text-[#3F6147] shrink-0">
            <Cookie size={15} strokeWidth={2} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">{t("cookie.title", { defaultValue: "We use cookies" })}</p>
            <p className="mt-1 text-xs text-[#5F6B61] leading-relaxed">
              {t("cookie.body", {
                defaultValue:
                  "Essential cookies keep your login session active. Optional analytics cookies help us understand which offers people care about — we'll only set them with your consent.",
              })}
            </p>
            <p className="mt-1 text-[11px] text-[#5F6B61]">
              <Link to="/datenschutz" className="underline hover:text-[#5B8C66]" data-testid="cookie-privacy-link">
                {t("cookie.privacy_link", { defaultValue: "Read our privacy policy" })}
              </Link>
            </p>

            {showDetails && (
              <ul className="mt-3 space-y-2 text-[11px] text-[#3A463C]" data-testid="cookie-details">
                <li className="flex justify-between gap-3">
                  <div>
                    <p className="font-semibold">{t("cookie.essential", { defaultValue: "Essential" })}</p>
                    <p className="text-[#5F6B61]">
                      {t("cookie.essential_desc", { defaultValue: "Authentication, language preference. Always on." })}
                    </p>
                  </div>
                  <span className="text-[#5B8C66] font-bold whitespace-nowrap">●●●</span>
                </li>
                <li className="flex justify-between gap-3">
                  <div>
                    <p className="font-semibold">{t("cookie.analytics", { defaultValue: "Analytics" })}</p>
                    <p className="text-[#5F6B61]">
                      {t("cookie.analytics_desc", { defaultValue: "Aggregated, anonymous page views (no personal data, no cross-site tracking)." })}
                    </p>
                  </div>
                </li>
              </ul>
            )}

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => persist(false)}
                className="btn-pill px-3 py-2 text-xs font-semibold border border-[#DED7C7] bg-white hover:border-[#5B8C66] hover:text-[#5B8C66]"
                data-testid="cookie-essential-only"
              >
                {t("cookie.essential_only", { defaultValue: "Essential only" })}
              </button>
              <button
                type="button"
                onClick={() => persist(true)}
                className="btn-pill px-3 py-2 text-xs font-bold bg-[#5B8C66] text-white hover:bg-[#3F6147]"
                data-testid="cookie-accept-all"
              >
                {t("cookie.accept_all", { defaultValue: "Accept all" })}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              className="mt-2 text-[11px] text-[#5F6B61] hover:text-[#5B8C66] underline"
              data-testid="cookie-toggle-details"
            >
              {showDetails
                ? t("cookie.hide_details", { defaultValue: "Hide details" })
                : t("cookie.show_details", { defaultValue: "Customize" })}
            </button>
          </div>
          <button
            type="button"
            onClick={() => persist(false)}
            aria-label={t("cookie.close", { defaultValue: "Decline" })}
            className="text-[#5F6B61] hover:text-[#1F2A24] shrink-0"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Helper for analytics scripts: returns current consent state or null. */
export function getCookieConsent() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v ? JSON.parse(v) : null;
  } catch (err) {
    devWarn("CookieBanner: getCookieConsent failed", err);
    return null;
  }
}
